import React, { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  HouseAvailabilityGlobalCorporateEveningOutputDto,
  HouseAvailabilityGlobalNightlyTripOutputDto,
  HouseAvailabilityGlobalOutputDto,
  HouseAvailabilityGlobalStateEnum,
  HouseAvailabilityGlobalStudyDayOutputDto,
} from '../../../../../../../../lib/graphql'
import { Icon } from '../../../../../../../atoms'
import NabooLogo from '../../../../../../../atoms/logos/NabooLogo'
import { formatMoney } from '../../../../../../house-options/shared/utils/format/format-money/format-money'
import { HouseContextGlobal } from '../../../../../helpers/assert-house-price-type'
import {
  getSeasonPriceKey,
  isWeekEnd,
} from '../../../../shared/calendar/utils/get-price-key-from-date'
import { BigCalendarFormDataGlobal } from '../../../../shared/calendar/utils/use-big-calendar-form'

type BigCalendarOptionsGlobalCellProps = {
  house: HouseContextGlobal
  cellDate: Date
}
export const BigCalendarOptionsGlobalCell = ({
  house,
  cellDate,
}: BigCalendarOptionsGlobalCellProps) => {
  const priceKey = getSeasonPriceKey({ date: cellDate, options: house.options })

  const nightlyTripPrice =
    house.options.nightlyTrip.options?.seasonalPrices[0][
      isWeekEnd({ date: cellDate }) ? 'weekEnd' : 'weekDay'
    ][priceKey]

  const studyDayPrice =
    house.options.studyDay.options?.seasonalPrices[0][priceKey]

  const nightlyTrip: BigCalendarFormDataGlobal['nightlyTrip'] = {
    state: HouseAvailabilityGlobalStateEnum.FREE,
    options: { price: nightlyTripPrice },
    isEnabled: house.options.nightlyTrip.isEnabled,
  }

  const studyDay: BigCalendarFormDataGlobal['studyDay'] = {
    state: HouseAvailabilityGlobalStateEnum.FREE,
    options: { price: studyDayPrice },
    isEnabled: house.options.studyDay.isEnabled,
  }

  const corporateEvening: BigCalendarFormDataGlobal['corporateEvening'] = {
    state: HouseAvailabilityGlobalStateEnum.FREE,
    isEnabled: house.options.corporateEvening.isEnabled,
  }

  return (
    <div className='flex flex-col items-start gap-2'>
      <BigCalendarNightlyTripChip nightlyTrip={nightlyTrip} house={house} />
      <BigCalendarStudyDayChip studyDay={studyDay} house={house} />
      <BigCalendarCorporateEveningChip
        corporateEvening={corporateEvening}
        house={house}
      />
    </div>
  )
}

type BigCalendarAvailabilityGlobalCellProps = {
  availability: HouseAvailabilityGlobalOutputDto
  house: HouseContextGlobal
}
export const BigCalendarAvailabilityGlobalCell = ({
  availability,
  house,
}: BigCalendarAvailabilityGlobalCellProps) => {
  const { nightlyTrip, studyDay, corporateEvening } = availability

  const isFullyBookedOrLocked = (
    [
      'corporateEvening',
      'nightlyTrip',
      'studyDay',
    ] as (keyof HouseAvailabilityGlobalOutputDto)[]
  ).every(
    (key) =>
      availability?.[key]?.state !== HouseAvailabilityGlobalStateEnum.FREE,
  )

  return (
    <>
      {isFullyBookedOrLocked ? (
        <div className='flex flex-col items-center text-center text-xs font-bold text-grey-700'>
          <FormattedMessage
            defaultMessage='Indisponibilité'
            id='ui.BigCalendarAvailabilityGlobalCell.unavailability'
          />
          <NabooLogo width={60} />
        </div>
      ) : (
        <div className='flex flex-col items-center gap-1'>
          <BigCalendarNightlyTripChip
            nightlyTrip={nightlyTrip}
            house={house}
            colorize={true}
          />
          <BigCalendarStudyDayChip
            studyDay={studyDay}
            house={house}
            colorize={true}
          />
          <BigCalendarCorporateEveningChip
            corporateEvening={corporateEvening}
            house={house}
            colorize={true}
          />
        </div>
      )}
    </>
  )
}

type BigCalendarNightlyTripChipProps = Pick<
  BigCalendarChipWrapperProps,
  'colorize'
> &
  Pick<BigCalendarOptionsGlobalCellProps, 'house'> & {
    nightlyTrip: HouseAvailabilityGlobalNightlyTripOutputDto
  }
const BigCalendarNightlyTripChip = ({
  nightlyTrip,
  house,
  colorize,
}: BigCalendarNightlyTripChipProps) => {
  if (!house.options.nightlyTrip.isEnabled) {
    return null
  }

  return (
    <BigCalendarChipWrapper state={nightlyTrip.state} colorize={colorize}>
      <Icon name='moon' size={12} />

      <span>
        {formatMoney({
          value: nightlyTrip.options?.price,
          currency: house.currencyArea.currency,
          options: { hideDecimals: true, applyFactor: true },
        })}
      </span>
    </BigCalendarChipWrapper>
  )
}

type BigCalendarStudyDayChipProps = Pick<
  BigCalendarChipWrapperProps,
  'colorize'
> &
  Pick<BigCalendarOptionsGlobalCellProps, 'house'> & {
    studyDay: HouseAvailabilityGlobalStudyDayOutputDto
  }
const BigCalendarStudyDayChip = ({
  studyDay,
  house,
  colorize,
}: BigCalendarStudyDayChipProps) => {
  if (!house.options.studyDay.isEnabled) {
    return null
  }

  return (
    <BigCalendarChipWrapper state={studyDay.state} colorize={colorize}>
      <Icon name='sun' size={12} />

      <span>
        {formatMoney({
          value: studyDay.options?.price,
          currency: house.currencyArea.currency,
          options: { hideDecimals: true, applyFactor: true },
        })}
      </span>
    </BigCalendarChipWrapper>
  )
}

type BigCalendarCorporateEveningChipProps = Pick<
  BigCalendarChipWrapperProps,
  'colorize'
> &
  Pick<BigCalendarOptionsGlobalCellProps, 'house'> & {
    corporateEvening: HouseAvailabilityGlobalCorporateEveningOutputDto
  }
const BigCalendarCorporateEveningChip = ({
  corporateEvening,
  house,
  colorize,
}: BigCalendarCorporateEveningChipProps) => {
  if (!house.options.corporateEvening.isEnabled) {
    return null
  }

  return (
    <BigCalendarChipWrapper state={corporateEvening.state} colorize={colorize}>
      <Icon name='celebration' size={12} />
      <span>-</span>
    </BigCalendarChipWrapper>
  )
}

type BigCalendarChipWrapperProps = {
  children: ReactNode
  state: HouseAvailabilityGlobalStateEnum
  colorize?: boolean
}
const BigCalendarChipWrapper = ({
  children,
  state,
  colorize,
}: BigCalendarChipWrapperProps) => {
  const bgColor = colorize ? 'bg-primary-400' : 'bg-grey-400'

  return (
    <div
      className={`flex h-5 w-16 items-center justify-center gap-1 rounded-lg ${bgColor} px-2 py-1 text-xs`}
    >
      <>
        {children}

        {state !== HouseAvailabilityGlobalStateEnum.FREE ? (
          <span className='-mr-4 -mt-4 rounded-full bg-grey-600 p-0.5'>
            <Icon name='lock' size={10} color='var(--white)' />
          </span>
        ) : null}
      </>
    </div>
  )
}
